import React, { useEffect, useState } from 'react';
import io from "socket.io-client";
import { 
  Table, Button, Input, Space, Typography, Tag, 
  message, Popconfirm, Switch, Card, Modal, Select, Avatar, Form, Tooltip
} from 'antd';
import { 
  UserAddOutlined, DeleteOutlined, SettingOutlined, EditOutlined,
  UserOutlined, CheckCircleOutlined, StopOutlined 
} from '@ant-design/icons';
import { API_BASE, SOCKET_URL } from '../../config/api';
import CreateEmployee from './CreateEmployee';
import { getAuthHeaders } from '../../utils/auth';

const { Title } = Typography;
const { Option } = Select;

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]); // Para el selector de asignación
  const [loading, setLoading] = useState(true);
  
  // Estados para creación
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  // Estados para asignar servicios
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [savingEmployee, setSavingEmployee] = useState(false);
  const [editForm] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [empRes, servRes] = await Promise.all([
        fetch(`${API_BASE}/employees`),
        fetch(`${API_BASE}/services`) // Asumiendo que esta es tu ruta de servicios
      ]);
      const empData = await empRes.json();
      const servData = await servRes.json();

      if (!empRes.ok) {
        throw new Error(empData?.message || "Error al cargar empleados");
      }

      if (!servRes.ok) {
        throw new Error(servData?.message || "Error al cargar servicios");
      }

      setEmployees(Array.isArray(empData) ? empData : []);
      setServices(Array.isArray(servData) ? servData : []);
    } catch (error) {
      console.error("fetchData empleados:", error);
      setEmployees([]);
      setServices([]);
      message.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const socket = io(SOCKET_URL);

    const handleEmployeesUpdate = () => {
      fetchData();
    };

    const handleCatalogUpdate = () => {
      fetchData();
    };

    socket.on("employees:updated", handleEmployeesUpdate);
    socket.on("catalog:updated", handleCatalogUpdate);

    return () => {
      socket.off("employees:updated", handleEmployeesUpdate);
      socket.off("catalog:updated", handleCatalogUpdate);
      socket.disconnect();
    };
  }, []);

 
  const handleToggle = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/employees/${id}/toggle`, {
        method: "PATCH",
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        message.success("Estado actualizado");
        fetchData();
      }
    } catch (err) {
      message.error("Error al cambiar estado");
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/employees/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        message.success("Empleado eliminado");
        fetchData();
      }
    } catch (err) {
      message.error("Error al eliminar");
    }
  };

  const handleAssignServices = async () => {
    try {
      const res = await fetch(`${API_BASE}/employees/${selectedEmployee.id}/services`, {
        method: "POST",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ serviceIds: selectedServices })
      });
      if (res.ok) {
        message.success("Servicios asignados");
        setIsAssignModalOpen(false);
        setSelectedServices([]);
        fetchData();
      }
    } catch (err) {
      message.error("Error al asignar");
    }
  };

  const openEditModal = (employee) => {
    setEditingEmployee(employee);
    editForm.setFieldsValue({
      name: employee?.name || "",
      email: employee?.email || "",
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateEmployee = async () => {
    try {
      const values = await editForm.validateFields();
      setSavingEmployee(true);

      const res = await fetch(`${API_BASE}/employees/${editingEmployee.id}`, {
        method: "PATCH",
        headers: getAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          name: values.name,
          email: values.email,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "No se pudo actualizar el empleado");
      }

      message.success("Empleado actualizado");
      setIsEditModalOpen(false);
      setEditingEmployee(null);
      editForm.resetFields();
      fetchData();
    } catch (error) {
      message.error(error.message || "Error al actualizar empleado");
    } finally {
      setSavingEmployee(false);
    }
  };

  // --- COLUMNAS DE LA TABLA ---

  const columns = [
    {
      title: 'Empleado',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar 
            style={{ backgroundColor: record?.background_color }} 
            icon={<UserOutlined />} 
          />
          <b style={{ color: record.active ? '#000' : '#bfbfbf' }}>{text}</b>
        </Space>
      )
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || <Tag color="default">Sin email</Tag>,
    },
    {
      title: 'Servicios Asignados',
      dataIndex: 'services',
      key: 'services',
      render: (services) => (
        <>
          {services?.map(s => (
            <Tag color="blue" key={s.id} style={{ marginBottom: '4px' }}>
              {s.name}
            </Tag>
          ))}
          {(!services || services.length === 0) && <Tag color="default">Sin servicios</Tag>}
        </>
      )
    },
    {
      title: 'Estado',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      render: (active, record) => (
        <Switch 
          checked={active} 
          onChange={() => handleToggle(record.id)} 
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<StopOutlined />}
        />
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 320,
      render: (_, record) => (
        <Space>
          <Tooltip title="Asignar servicios">
            <Button 
              icon={<SettingOutlined />} 
              onClick={() => {
                setSelectedEmployee(record);
                setSelectedServices(record.services?.map(s => s.id) || []);
                setIsAssignModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Editar empleado">
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            >
              Editar empleado
            </Button>
          </Tooltip>
          <Popconfirm
            title="¿Eliminar empleado?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space style={{ marginBottom: 24, justifyContent: 'space-between', width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>Gestión de Empleados</Title>
          <Button 
            type="primary" 
            icon={<UserAddOutlined />} 
            onClick={() => setIsAddModalOpen(true)}
          >
            Nuevo Empleado
          </Button>
        </Space>

        <Table 
          dataSource={employees} 
          columns={columns} 
          rowKey="id" 
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* MODAL CREAR */}
      <Modal
        title="Agregar Nuevo Empleado"
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        footer={null} // <--- Esto elimina OnText y OffText (Aceptar/Cancelar)
        destroyOnClose
      >
       <CreateEmployee fetchData= {fetchData}/>
        <p style={{ marginTop: 10, color: '#8c8c8c' }}>
          Se le asignará un color aleatorio automáticamente.
        </p>
      </Modal>

      {/* MODAL ASIGNAR SERVICIOS */}
      <Modal
        title={`Asignar Servicios a: ${selectedEmployee?.name}`}
        open={isAssignModalOpen}
        onOk={handleAssignServices}
        onCancel={() => setIsAssignModalOpen(false)}
        okText="Guardar Cambios"
        width={600}
      >
        <p>Selecciona los servicios que este empleado puede realizar:</p>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Seleccionar servicios"
          value={selectedServices}
          onChange={(values) => setSelectedServices(values)}
          optionFilterProp="children"
        >
          {services.map(s => (
            <Option key={s.id} value={s.id}>{s.name}</Option>
          ))}
        </Select>
      </Modal>

      <Modal
        title={`Editar empleado${editingEmployee?.name ? `: ${editingEmployee.name}` : ""}`}
        open={isEditModalOpen}
        onOk={handleUpdateEmployee}
        onCancel={() => {
          setIsEditModalOpen(false);
          setEditingEmployee(null);
          editForm.resetFields();
        }}
        okText="Guardar cambios"
        confirmLoading={savingEmployee}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="Nombre del empleado"
            name="name"
            rules={[{ required: true, message: "El nombre es obligatorio" }]}
          >
            <Input placeholder="Ej: Juan Pérez" />
          </Form.Item>

          <Form.Item
            label="Email del empleado"
            name="email"
            rules={[
              { required: true, message: "El email es obligatorio" },
              { type: "email", message: "Ingresá un email válido" },
            ]}
          >
            <Input placeholder="Ej: juan@estudioisaac.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Employees;
